import React from 'react';

import { ThemingProps } from '@chakra-ui/styled-system';
import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  ToggleButtonProps,
} from '@saas-ui-pro/react';

import { ButtonGroupContext, type ButtonGroupVariant } from './useButtonGroup';

export type ButtonGroupValue = string | number;
export type ButtonGroupType = 'radio' | 'checkbox';

// Extended ButtonGroup props that include our custom variants
export interface ButtonGroupProps
  extends Omit<
    ToggleButtonGroupProps,
    'variant' | 'type' | 'onChange' | 'defaultValue' | 'value'
  > {
  /**
   * The visual style of the button group
   * @default 'attached'
   */
  variant?: ButtonGroupVariant;
  /**
   * The size of the buttons in the group
   * @default 'md'
   */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /**
   * The spacing between buttons when variant is 'separated'
   * @default '2'
   */
  spacing?: string | number;
  /**
   * Whether the button group is disabled
   * @default false
   */
  isDisabled?: boolean;
  /**
   * The color scheme of the buttons
   * @default 'blue'
   */
  colorScheme?: ThemingProps['colorScheme'];
  /**
   * Whether the buttons should be full width
   * @default false
   */
  isFullWidth?: boolean;
  /**
   * The type of the buttons
   * @default 'radio'
   */
  type?: ButtonGroupType;
  /**
   * The onChange function
   * @default undefined
   */
  onChange?: (value: ButtonGroupValue | ButtonGroupValue[]) => void;
  /**
   * The default value of the button group
   * @default undefined
   */
  defaultValue?: ButtonGroupValue | ButtonGroupValue[];
  /**
   * The controlled value of the button group
   * @default undefined
   */
  value?: ButtonGroupValue | ButtonGroupValue[];
}

// Button props that work within the group context
export interface ButtonGroupButtonProps
  extends Omit<ToggleButtonProps, 'variant'> {
  /**
   * The visual style of the individual button
   * @default 'solid'
   */
  variant?: 'solid' | 'outline' | 'ghost' | 'link' | 'unstyled';
  /**
   * Whether this button is active/selected
   * @default false
   */
  isActive?: boolean;
}

const CHIP_CHECKED_STYLES = {
  bg: 'glass-items.bg-hover',
  color: 'glass-items.icon',
  fontWeight: 'semibold',
  borderColor: 'glass-items.border-hover',
  _hover: {
    bg: 'glass-items.bg-hover',
    color: 'glass-items.icon',
  },
} as const;

// Button component that works within the group
export const ButtonGroupButton: React.FC<ButtonGroupButtonProps> = ({
  variant = 'solid',
  isActive,
  ...props
}) => {
  const context = React.useContext(ButtonGroupContext);
  const isChip = context.variant === 'chip';

  const getButtonVariant = () => {
    switch (context.variant) {
      case 'attached':
        return 'solid';
      case 'attached-outline':
      case 'separated-outline':
      case 'chip':
        return 'outline';
      case 'separated':
        return variant;
      default: {
        const _exhaustive: never = context.variant;
        return _exhaustive;
      }
    }
  };

  return (
    <ToggleButton
      variant={getButtonVariant()}
      size={context.size}
      colorScheme={context.colorScheme}
      isDisabled={context.isDisabled}
      width={context.isFullWidth ? 'full' : undefined}
      isActive={isActive}
      borderColor='glass-items.border'
      color='glass-items.icon'
      {...props}
      borderRadius={isChip ? 'full' : 'lg'}
      fontWeight={isChip ? 'medium' : undefined}
      bg={isChip ? 'glass-items.bg' : undefined}
      px={isChip ? 3 : undefined}
      whiteSpace={isChip ? 'nowrap' : undefined}
      transitionProperty={
        isChip ? 'background-color, color, border-color' : undefined
      }
      transitionDuration={isChip ? '150ms' : undefined}
      transitionTimingFunction={
        isChip ? 'cubic-bezier(0.2, 0, 0, 1)' : undefined
      }
      _checked={
        isChip
          ? CHIP_CHECKED_STYLES
          : {
              bg: 'glass-items.bg-active',
              color: 'glass-items.icon',
            }
      }
      _hover={
        isChip
          ? {
              bg: { md: 'glass-items.bg-active' },
              color: { md: 'glass-items.icon' },
            }
          : {
              bg: { md: 'glass-items.bg-hover' },
              color: { md: 'glass-items.icon-hover' },
            }
      }
    />
  );
};

ButtonGroupButton.displayName = 'ButtonGroupButton';

// Main ButtonGroup component
export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  variant = 'attached',
  size = 'md',
  spacing = 2,
  isDisabled = false,
  colorScheme = 'blue',
  isFullWidth = false,
  children,
  type = 'radio',
  onChange,
  defaultValue,
  value,
  ...props
}) => {
  const contextValue = {
    variant,
    size,
    colorScheme,
    isDisabled,
    isFullWidth,
  };

  const isChip = variant === 'chip';
  const groupSpacing = isChip
    ? 1.5
    : variant.includes('separated')
      ? spacing
      : 0;

  return (
    <ButtonGroupContext.Provider value={contextValue}>
      <ToggleButtonGroup
        size={size}
        isDisabled={isDisabled}
        spacing={groupSpacing}
        width={isFullWidth ? 'full' : undefined}
        type={type}
        bg={isChip ? 'transparent' : 'glass-items.bg'}
        borderColor={isChip ? 'transparent' : 'glass-items.border'}
        flexWrap={isChip ? 'wrap' : undefined}
        defaultValue={defaultValue}
        value={value}
        onChange={onChange}
        {...(isChip ? { isAttached: false as const } : {})}
        {...props}
      >
        {children}
      </ToggleButtonGroup>
    </ButtonGroupContext.Provider>
  );
};

ButtonGroup.displayName = 'ButtonGroup';
